// ============================================
// 🔥 SUPABASE CLIENT - Configuração do Supabase
// ============================================

import { createClient } from '@supabase/supabase-js';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../config/constants';

// 🔥 Cliente Supabase com configuração de Realtime
const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  realtime: {
    timeout: 60000,
    heartbeatIntervalMs: 30000,
    params: {
      eventsPerSecond: 10
    }
  }
});

// ============================================================
// MULTI-TENANT: Helpers para isolamento por tenant
// ============================================================
// Importação lazy via getter para evitar ciclo circular.
// useAppStore não importa supabaseClient, mas a ordem de módulos
// pode variar — o getter garante que o import seja resolvido
// apenas quando a função é chamada (não durante o module load).
// ============================================================

let _getStore = null;
const getStore = () => {
  if (!_getStore) {
    // eslint-disable-next-line
    _getStore = require('../store/useAppStore').default;
  }
  return _getStore;
};

/**
 * Retorna o tenantId do usuário logado (do Zustand store).
 * Seguro para uso fora de componentes React.
 *
 * @returns {string|null}
 */
export const getCurrentTenantId = () => getStore().getState().tenantId ?? null;

/**
 * Adiciona filtro de tenant_id a uma query Supabase.
 * Aplica-se após .select(), .update() ou .delete().
 *
 * @example
 *   // SELECT
 *   const { data } = await withTenant(supabase.from('clientes').select('*'));
 *   // UPDATE
 *   await withTenant(supabase.from('clientes').update({ x: 1 }).eq('id', id));
 *
 * @param {Object} query - Query já inicializada
 * @returns {Object} Query com .eq('tenant_id', tenantId)
 */
export const withTenant = (query) => {
  const tenantId = getCurrentTenantId();
  if (!tenantId) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[withTenant] tenantId is null — RLS will filter, but the store may not be hydrated yet.');
    }
    return query;
  }
  return query.eq('tenant_id', tenantId);
};

/**
 * Injeta tenant_id em dados para INSERT / UPSERT.
 *
 * @example
 *   await supabase.from('clientes').insert(injectTenantId({ nome: 'João' }));
 *
 * @param {Object|Array} data
 * @returns {Object|Array}
 */
export const injectTenantId = (data) => {
  const tenantId = getCurrentTenantId();
  if (!tenantId) return data;
  if (Array.isArray(data)) return data.map(d => ({ ...d, tenant_id: tenantId }));
  return { ...data, tenant_id: tenantId };
};

/**
 * Cabeçalhos para as Serverless Functions em /api, que exigem login
 * (api/_autenticacao.js). Leva o access_token da sessão atual.
 */
export const cabecalhosComLogin = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${session?.access_token || ''}`
  };
};

export default supabase;
